// THE ONE LOG LINE, READ BACK. internal/logline (Go) writes every line of the panel, the mail gateway
// and the commands other units run as one JSON object with one stamp:
//
//   {"time":"2026-10-09T23:32:51.482913Z","seq":41,"node":"node10","boot":"20261009T233251.482913Z-4711",
//    "level":"warn","svc":"ozpanel","component":"cluster","message":"…", …the caller's fields}
//
// The console's log views get those lines from the journal (`journalctl -o short-iso`), so each row
// is the journal's own prefix — WHEN JOURNALD RECEIVED IT, to the second, per stream — followed by
// the line. This module is the one place that takes a row apart, so every log view shows the same
// thing: the LINE's time (when it was written, to the microsecond), not the journal's.
//
// Parsing is defensive by construction: a row that is not the one line — another daemon's journal
// (PostgreSQL, tinc, a tenant's container), a Go runtime fatal (written straight to descriptor 2,
// never stamped), JSON that was cut — is returned as `raw` and displayed as it arrived. Nothing is
// ever dropped here; a view that hides rows does so by a filter the reader chose, and says how many.
//
// Pure functions, no React: LogLines.tsx renders what these return, logLine.test.ts holds them to
// real lines the Go tests produce.

/** The stamp's exact shape: UTC, RFC 3339, six fractional digits — logline.TimeFormat. */
const STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;

/** `journalctl -o short-iso`: "2026-10-09T23:32:56+0000 node10 ozpanel[4711]: <message>". */
const JOURNAL_PREFIX = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:[.,]\d+)?(?:Z|[+-]\d{2}:?\d{2})) (\S+) ([^\s:[]+)(?:\[(\d+)\])?: ?(.*)$/s;

/** The keys the writer and the logger put on every line; everything else is the caller's. */
const OWN_KEYS = new Set(["time", "seq", "node", "boot", "level", "svc", "component", "message", "decision"]);

export type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "fatal" | "panic" | "";

/** One line that IS the one line. */
export interface OneLine {
  /** When it was written: the node's clock, UTC, microseconds. Sorts as text. */
  time: string;
  /** This process's line number, from 1 — orders lines inside one microsecond; a gap is a lost line. */
  seq: number;
  /** The node that WROTE the line. */
  node: string;
  /** This run of the process; a seq means nothing across two of them. */
  boot: string;
  level: LogLevel;
  svc: string;
  component: string;
  message: string;
  /** On the decision path (logline.Decision): written whatever the level filter says. */
  decision: boolean;
  /** The caller's own fields, in the order they were written. */
  fields: [string, unknown][];
}

/** One row of a log view. */
export interface LogEntry {
  /** The row exactly as the API returned it. */
  raw: string;
  /** Position in the response — the journal's own order, the tie-break of last resort. */
  index: number;
  /** The journal's prefix, when the row has one. `received` is when journald took the line. */
  received?: string;
  host?: string;
  ident?: string;
  pid?: string;
  /** The parsed line; undefined when the row is not the one line (then `raw` is what is shown). */
  line?: OneLine;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** parseOneLine reads a payload as the one line, or returns undefined — never throws. */
export function parseOneLine(payload: string): OneLine | undefined {
  const text = payload.trim();
  // Cheap refusal first: the writer always begins a line with its time.
  if (!text.startsWith('{"time":"') || !text.endsWith("}")) return undefined;
  let obj: unknown;
  try {
    obj = JSON.parse(text);
  } catch {
    return undefined; // cut, or not JSON after all
  }
  if (!isRecord(obj)) return undefined;
  const { time, seq, node, boot } = obj;
  // The whole stamp or it is not the line: a lookalike with a `time` of its own (another daemon's
  // JSON log) must not be given a place in the order as if the one writer had stamped it.
  if (typeof time !== "string" || !STAMP.test(time)) return undefined;
  if (typeof seq !== "number" || !Number.isInteger(seq) || seq < 1) return undefined;
  if (typeof node !== "string" || typeof boot !== "string" || boot === "") return undefined;
  const str = (k: string) => (typeof obj[k] === "string" ? (obj[k] as string) : "");
  return {
    time,
    seq,
    node,
    boot,
    level: str("level") as LogLevel,
    svc: str("svc"),
    component: str("component"),
    message: str("message"),
    decision: obj.decision === true,
    fields: Object.entries(obj).filter(([k]) => !OWN_KEYS.has(k)),
  };
}

/** parseLogEntry takes one row apart: the journal's prefix if it has one, then the line if it is one. */
export function parseLogEntry(raw: string, index = 0): LogEntry {
  const entry: LogEntry = { raw, index };
  let payload = raw;
  const m = JOURNAL_PREFIX.exec(raw);
  if (m) {
    entry.received = m[1];
    entry.host = m[2];
    entry.ident = m[3];
    entry.pid = m[4];
    payload = m[5] ?? "";
  }
  entry.line = parseOneLine(payload);
  return entry;
}

/** parseLogRows parses a response: an array of rows, or one string of newline-separated rows. */
export function parseLogRows(rows: string[] | string | null | undefined): LogEntry[] {
  const list = typeof rows === "string" ? rows.split("\n") : (rows ?? []);
  const out: LogEntry[] = [];
  for (const row of list) {
    if (row.trim() === "") continue; // an empty row is the response's trailing newline, not a line
    out.push(parseLogEntry(row, out.length));
  }
  return out;
}

/**
 * orderEntries puts the rows in the order they were WRITTEN: by the line's `time`, then — inside
 * one `boot` — by `seq`. The journal's order is not that order: it is the order journald READ the
 * lines, and it reads stdout and stderr (and each unit) as separate streams.
 *
 * A row that is not the one line has no time of its own to sort by, so it stays where the journal
 * put it relative to the line before it: it travels with that line (a runtime fatal's trace stays
 * under the last thing the process logged). Rows before the first parsed line stay first.
 *
 * (time, boot, seq, index) is a total order, so the result does not depend on the input's order —
 * which is what makes it a MERGE when rows from several nodes are passed in together. Not handled:
 * a wall clock stepped backwards inside one boot sorts by the stepped time, where seq alone would
 * be right; `outOfSequence` reports the rows where that happened rather than hiding it.
 */
export function orderEntries(entries: LogEntry[]): LogEntry[] {
  type Keyed = { e: LogEntry; anchor: OneLine | undefined };
  let anchor: OneLine | undefined;
  const keyed: Keyed[] = entries.map((e) => {
    if (e.line) anchor = e.line;
    return { e, anchor: e.line ?? anchor };
  });
  return keyed
    .sort((a, b) => {
      const x = a.anchor;
      const y = b.anchor;
      if (!x || !y) return x === y ? a.e.index - b.e.index : x ? 1 : -1;
      if (x.time !== y.time) return x.time < y.time ? -1 : 1;
      if (x.boot !== y.boot) return x.boot < y.boot ? -1 : 1;
      if (x.seq !== y.seq) return x.seq - y.seq;
      return a.e.index - b.e.index;
    })
    .map((k) => k.e);
}

/**
 * outOfSequence returns the parsed rows whose seq is LOWER than a row of the same boot placed
 * before them — time and seq disagree there (the node's clock was stepped backwards, or two rows
 * carry one microsecond in the other order). Shown as a note, because an order the reader is about
 * to rely on should say where it is unsure.
 */
export function outOfSequence(ordered: LogEntry[]): LogEntry[] {
  const high = new Map<string, number>();
  const out: LogEntry[] = [];
  for (const e of ordered) {
    if (!e.line) continue;
    const seen = high.get(e.line.boot) ?? 0;
    if (e.line.seq < seen) out.push(e);
    else high.set(e.line.boot, e.line.seq);
  }
  return out;
}

/** The line's time to the millisecond, for a row: "10-09 23:32:51.482". */
export function timeToMillis(time: string): string {
  // Sliced, not parsed: a Date holds milliseconds and would drop the digits the line exists for.
  return `${time.slice(5, 10)} ${time.slice(11, 23)}`;
}

/** The line's time in full, for hover and the detail: "2026-10-09 23:32:51.482913 UTC". */
export function timeToMicros(time: string): string {
  return `${time.slice(0, 10)} ${time.slice(11, 26)} UTC`;
}

/** How long the journal took to receive the line, in whole seconds — the journal's stamp has no more. */
export function receiveDelaySeconds(e: LogEntry): number | undefined {
  if (!e.line || !e.received) return undefined;
  const got = Date.parse(e.received.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
  const written = Date.parse(e.line.time);
  if (Number.isNaN(got) || Number.isNaN(written)) return undefined;
  return Math.max(0, Math.floor(got / 1000) - Math.floor(written / 1000));
}

/** A field as `key=value`: a string as it is, anything else as JSON. */
export function fieldText(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value) ?? String(value);
}

/** Chips are a glance; a long value is cut there and whole in the detail. */
export function fieldChip(key: string, value: unknown, max = 64): string {
  const text = fieldText(value);
  return `${key}=${text.length > max ? `${text.slice(0, max - 1)}…` : text}`;
}

export type LevelTone = "ok" | "warn" | "bad" | "neutral" | "accent";

/** The level as a tone the status pill has. An unknown level is neutral, never coerced to fine. */
export function levelTone(level: string): LevelTone {
  switch (level) {
    case "error":
    case "fatal":
    case "panic":
      return "bad";
    case "warn":
      return "warn";
    default:
      return "neutral";
  }
}

const RANK = new Map([["trace", 0], ["debug", 1], ["info", 2], ["warn", 3], ["error", 4], ["fatal", 5], ["panic", 6]]);

/** A level's rank; a line with no level (or one this does not know) ranks as info, as the writer treats it. */
function rank(level: string): number {
  return RANK.get(level) ?? 2;
}

/** What a log view's filter bar can ask for. "" means "do not filter on this". */
export interface LogFilter {
  /** "" all, "warn" warn and above, "error" error and above, "decision" the decision path only. */
  level: "" | "info" | "warn" | "error" | "decision";
  component: string;
}

export const NO_FILTER: LogFilter = { level: "", component: "" };

export function filterActive(f: LogFilter): boolean {
  return f.level !== "" || f.component !== "";
}

/**
 * applyFilter keeps the parsed rows the filter asks for. A row that is not the one line has no
 * level and no component, so an active filter cannot be asked about it: it is left out and COUNTED
 * (`hiddenRaw`), and the view says so. With no filter active every row is kept.
 */
export function applyFilter(entries: LogEntry[], f: LogFilter): { shown: LogEntry[]; hiddenRaw: number } {
  if (!filterActive(f)) return { shown: entries, hiddenRaw: 0 };
  let hiddenRaw = 0;
  const shown = entries.filter((e) => {
    if (!e.line) {
      hiddenRaw++;
      return false;
    }
    if (f.component !== "" && e.line.component !== f.component) return false;
    if (f.level === "decision") return e.line.decision;
    if (f.level !== "") return rank(e.line.level) >= rank(f.level);
    return true;
  });
  return { shown, hiddenRaw };
}

/** The components present, for the filter's choices — in name order, "" (none named) left out. */
export function componentsOf(entries: LogEntry[]): string[] {
  const set = new Set<string>();
  for (const e of entries) if (e.line?.component) set.add(e.line.component);
  return [...set].sort();
}
